import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useQuery } from "@tanstack/react-query";
import ContentPage from "@/components/contentPage";
import RolesManagement from "@/components/organizations/rolesManagement";
import DeploymentGrantsManagement from "@/components/organizations/deploymentGrantsManagement";
import UserManagement from "@/components/organizations/userManagement";
import Card from "@/components/cards/card";
import {
  getDataFromAllPages,
} from "@/apiUtils";
import { AvailableRoles } from "@/apiClient";

const organizationDetailQueryOptions = (api: any, id: string) =>
  queryOptions({
    queryKey: ["organization", id],
    queryFn: () => api.users.getOrganizationDetail({ organizationId: id }),
    enabled: !!id,
  });

const organizationGrantsQueryOptions = (api: any, id: string) =>
  queryOptions({
    queryKey: ["organizationDeploymentGrants", id],
    queryFn: () =>
      getDataFromAllPages({
        fn: api.users.listDeploymentGrantsForOrganization.bind(api.users),
        args: {
          organizationId: id,
        },
      }),
    enabled: !!id,
  });

export const Route = createFileRoute("/organizations/$id/")({
  component: OrganizationDetail,
  loader: async ({ context: { queryClient, api }, params: { id } }) => {
    await Promise.all([
      queryClient.prefetchQuery(organizationDetailQueryOptions(api, id)),
      queryClient.prefetchQuery(organizationGrantsQueryOptions(api, id)),
    ]);
  },
});

function OrganizationDetail() {
  const { id } = Route.useParams();
  const { api } = Route.useRouteContext();

  const {
    data: organization,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["organization", id],
    queryFn: () => api.users.getOrganizationDetail({ organizationId: id }),
    enabled: !!id,
  });

  const { data: deploymentGrants, isLoading: grantsLoading } = useQuery({
    queryKey: ["organizationDeploymentGrants", id],
    queryFn: () =>
      getDataFromAllPages({
        fn: api.users.listDeploymentGrantsForOrganization.bind(api.users),
        args: {
          organizationId: id,
        },
      }),
    enabled: !!id,
  });

  if (isLoading || grantsLoading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;
  if (!organization || !deploymentGrants) return <div>Loading...</div>;

  return (
    <ContentPage title={`Organization: ${organization.name}`}>
      <div className="max-w-4xl space-y-6">
        <Card>
          <h2 className="mb-4 text-2xl font-semibold text-blue-200">Details</h2>
          <div className="space-y-2">
            <p>
              <span className="font-medium">ID:</span> {organization.id}
            </p>
            <p>
              <span className="font-medium">Name:</span> {organization.name}
            </p>
          </div>
        </Card>

        <Card>
          <RolesManagement
            organizationId={organization.id}
            initialRoles={(organization.roles?.map((role: any) => role.name) || []) as AvailableRoles[]}
          />
        </Card>

        <Card>
          <DeploymentGrantsManagement
            organizationId={organization.id}
            initialGrants={deploymentGrants.map((grant) => grant.public_id)}
          />
        </Card>

        <Card>
          <UserManagement organizationId={organization.id} />
        </Card>
      </div>
    </ContentPage>
  );
}
